import {
  Button,
  Stack,
  Typography,
  Modal,
  ModalClose,
  DialogTitle,
  ModalDialog,
  DialogContent,
  buttonClasses,
} from "@mui/joy";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  DoneAllOutlined as DoneAllOutlinedIcon,
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import CheckListOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import { useRef, useMemo, useState, useEffect } from "react";
import { ValidationResponse, alertChecks } from "../../utility/validationChecker";

export default function AlertsChecker({
  expandedRow,
  isLoading,
  isError,
  checkResults,
  setExpandedRow,
}: {
  expandedRow: boolean;
  isLoading: boolean;
  checkResults: ValidationResponse[];
  isError: boolean;
  setExpandedRow: (expanded: boolean) => void;
}) {
  const expandableRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setModalOpen] = useState(false);

  const errorLines = useMemo(() => checkResults.flatMap((check) => check.affectedLines || []), [checkResults]);

  const expandableHeight = useMemo(
    () => (expandedRow && errorLines.length > 0 ? `${expandableRef.current?.scrollHeight}px` : "3.5rem"),
    [expandedRow, expandableRef, errorLines],
  );

  const failedChecks = useMemo(() => {
    return checkResults.filter((check) => !check.isValid).length;
  }, [checkResults]);

  function scrollToView(e: React.MouseEvent, elementId: number | undefined) {
    e.stopPropagation();
    if (elementId === undefined) return;
    const element = document.getElementById(`reservation-id-${elementId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function openModal(e: React.MouseEvent) {
    e.stopPropagation();
    setModalOpen(true);
  }

  useEffect(() => {
    if (errorLines.length !== 0) {
      document.getElementById("alerts-checker-container")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errorLines]);

  return (
    <>
      <Stack
        id="alerts-checker-container"
        ref={expandableRef}
        sx={{
          position: "relative",
          alignItems: "center",
          border: "1px solid #e1e6eb",
          width: "100%",
          height: expandableHeight,
          borderRadius: 10,
          overflow: "hidden",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Stack
          onClick={() => setExpandedRow(!expandedRow)}
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{
            minHeight: "3.5rem",
            paddingX: "1rem",
            backgroundColor: errorLines.length > 0 ? "#FEF2F2" : "#F8FAFC",
            width: "100%",
            transition: "all 0.3s ease-in-out",
            borderBottom: expandedRow ? "1px solid #e1e6eb" : "none",
            cursor: errorLines.length > 0 ? "pointer" : "default",
          }}
        >
          <Stack direction={"row"} gap={1}>
            {isLoading || checkResults.length === 0 ? (
              <InfoOutlinedIcon size="sm" sx={{ height: "1.3rem" }} />
            ) : failedChecks > 0 || isError ? (
              <ErrorOutlineOutlinedIcon color="danger" size="sm" sx={{ height: "1.3rem" }} />
            ) : (
              <DoneAllOutlinedIcon color="success" size="sm" sx={{ height: "1.3rem" }} />
            )}
            <Typography
              color={
                isLoading || checkResults.length === 0 ? "neutral" : failedChecks > 0 || isError ? "danger" : "success"
              }
              fontFamily={"sans-serif"}
              fontSize={"15px"}
            >
              {isError
                ? "Error validating alerts."
                : isLoading || checkResults.length === 0
                ? "Validating alerts..."
                : failedChecks > 0
                ? `${failedChecks} ${failedChecks === 1 ? "check" : "checks"} out of ${alertChecks?.length} ${
                    failedChecks === 1 ? "has" : "have"
                  } failed`
                : "All checks have passed"}
            </Typography>
          </Stack>
          <Button
            startDecorator={<CheckListOutlinedIcon />}
            onClick={(e) => openModal(e)}
            size="sm"
            variant="outlined"
            color="neutral"
            sx={{
              [`&.${buttonClasses.root}`]: {
                background: "#FFF !important",
              },
            }}
          >
            View checks
          </Button>
        </Stack>
        <Stack sx={{ width: "100%", backgroundColor: "#FFF", padding: "1rem" }}>
          {errorLines
            .sort((a, b) => {
              const A = a.bookingNumber;
              const B = b.bookingNumber;
              if (!A && B) return -1;
              if (!B && A) return 1;
              if (!A && !B) return 0;
              return A!.localeCompare(B!);
            })
            .map((error, index) => {
              return (
                <Stack
                  key={index}
                  direction={"row"}
                  alignItems={"center"}
                  gap={1}
                  sx={{
                    padding: "0.5rem",
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onClick={(e) => scrollToView(e, error.reservationId)}
                >
                  <ErrorOutlineOutlinedIcon color="danger" size="sm" />
                  <Typography fontFamily={"Inter"} fontSize={"14px"}>
                    {error.message} {error.vendorName ? `- ${error.vendorName}` : ""}{" "}
                    {error.bookingNumber ? ` (${error.bookingNumber})` : ""}
                  </Typography>
                </Stack>
              );
            })}
        </Stack>
      </Stack>
      <Modal keepMounted open={isModalOpen} onClose={() => setModalOpen(false)}>
        <ModalDialog sx={{ width: "60vw", maxHeight: "80vh", overflowY: "auto" }}>
          <ModalClose />
          <DialogTitle>Checks</DialogTitle>
          <DialogContent>
            <Stack sx={{ width: "100%" }}>
              {checkResults
                .sort((a, b) => (a.isValid === b.isValid ? 0 : a.isValid ? 1 : -1))
                .map((check, index) => {
                  return (
                    <Stack
                      key={index}
                      direction={"row"}
                      alignItems={"center"}
                      gap={1}
                      sx={{
                        padding: "0.5rem",
                        width: "100%",
                      }}
                      onClick={(e) => scrollToView(e, check.affectedLines?.[0]?.reservationId)}
                    >
                      {check.isValid ? (
                        <CheckCircleOutlineIcon color="success" size="sm" />
                      ) : (
                        <ErrorOutlineOutlinedIcon color="danger" size="sm" />
                      )}
                      <Typography fontFamily={"Inter"} fontSize={"14px"}>
                        {check.message}
                      </Typography>
                    </Stack>
                  );
                })}
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </>
  );
}
